import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Scan, Home, QrCode, CheckCircle2, XCircle, Camera, Upload, AlertTriangle, Car, Truck, Loader2 } from 'lucide-react';

export function PlateVerification({
  plateNumber,
  setPlateNumber,
  scanning,
  handleVerifyPlate,
  villaLookup,
  setVillaLookup,
  villaVehicles,
  villaDeliveryPasses,
  handleDeliveryApprove,
  deliveryEntryLoading,
  villaLookupLoading,
  handleVillaLookup,
  handleManualApproval,
  manualApprovalLoading,
  latestReading,
  setShowQRScanner,
  accessCode,
  setAccessCode,
  handleVerifyGatePass,
  verificationResult,
  gatePassVehicleNumber,
  setGatePassVehicleNumber,
  gatePassVehicleLoading,
  handleRecordGatePassVehicle,
  gatePassVisitorPhoto,
  setGatePassVisitorPhoto,
  handleGatePassPhotoUpload,
  gatePassEntryCompleted
}) {
  return (
    <Card className="shadow-xl border-0" data-testid="plate-scanner-card">
      <CardHeader className="bg-gradient-to-r from-gk-navy-light to-gk-navy text-white rounded-t-xl">
        <CardTitle className="flex items-center gap-2">
          <Scan className="w-5 h-5" />
          Manual Plate Verification
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="plate-number">License Plate Number</Label>
            <div className="flex gap-2">
              <Input
                id="plate-number"
                data-testid="plate-number-input"
                placeholder="Enter plate number (e.g., KA01AB1234)"
                value={plateNumber}
                onChange={(e) => setPlateNumber(e.target.value.toUpperCase())}
                className="font-mono text-lg"
                onKeyPress={(e) => e.key === 'Enter' && handleVerifyPlate()}
              />
              <Button
                data-testid="verify-plate-button"
                onClick={handleVerifyPlate}
                disabled={scanning}
                className="bg-gk-teal hover:bg-gk-teal-hover px-8"
              >
                {scanning ? 'Scanning...' : 'Verify'}
              </Button>
            </div>
          </div>

          {/* Villa Number Lookup */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="space-y-3">
              <Label htmlFor="villa-lookup" className="text-blue-800 font-semibold flex items-center gap-2">
                <Home className="w-4 h-4" />
                Search by Villa
                <span className="text-[10px] font-normal text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full ml-1">Vehicles + Delivery pre-approvals</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="villa-lookup"
                  data-testid="villa-lookup-input"
                  placeholder="e.g., A-101 — finds delivery pre-approvals & registered vehicles"
                  value={villaLookup}
                  onChange={(e) => setVillaLookup(e.target.value.toUpperCase())}
                  className="font-mono"
                  onKeyPress={(e) => e.key === 'Enter' && handleVillaLookup()}
                />
                <Button
                  data-testid="villa-lookup-button"
                  onClick={handleVillaLookup}
                  disabled={villaLookupLoading}
                  className="bg-blue-600 hover:bg-blue-700 px-6"
                >
                  {villaLookupLoading ? 'Loading...' : 'Lookup'}
                </Button>
              </div>

              {villaVehicles.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm font-semibold text-blue-800">
                    Registered Vehicles for Villa {villaLookup}:
                  </p>
                  {latestReading?.decision === 'RED' && (
                    <div className="p-2 bg-amber-100 border border-amber-300 rounded-lg">
                      <p className="text-xs text-amber-800 font-medium">
                        Access was denied. Select a vehicle to manually approve entry if the plate matches.
                      </p>
                    </div>
                  )}
                  <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                    <div className="space-y-2 p-2">
                      {villaVehicles.map((vehicle, idx) => (
                        <div key={vehicle.id || idx} className="flex items-center gap-3 p-2.5 rounded-lg border border-blue-100 hover:bg-blue-50 transition-colors">
                          {vehicle.vehicle_picture ? (
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}/api/vehicles/${vehicle.id}/picture`}
                              alt={vehicle.plate_number}
                              className="w-16 h-16 rounded-lg object-cover border border-slate-200 flex-shrink-0"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className={`w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0 ${vehicle.vehicle_type === '2-wheeler' ? 'bg-amber-100' : 'bg-blue-100'}`}>
                              <Car className={`w-7 h-7 ${vehicle.vehicle_type === '2-wheeler' ? 'text-amber-500' : 'text-blue-400'}`} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono font-bold text-sm text-slate-900">{vehicle.plate_number}</span>
                              <Badge variant="outline" className="text-[10px]">{vehicle.vehicle_type || 'Car'}</Badge>
                            </div>
                            {(vehicle.make || vehicle.vehicle_model) && (
                              <p className="text-sm font-medium text-slate-700 mt-0.5">{vehicle.make} {vehicle.vehicle_model}</p>
                            )}
                            <p className="text-xs text-slate-500">
                              {vehicle.owner_name}
                              {vehicle.color && <span className="ml-1.5 text-slate-400">| {vehicle.color}</span>}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            data-testid={`approve-entry-btn-${idx}`}
                            onClick={() => handleManualApproval(vehicle)}
                            disabled={manualApprovalLoading === vehicle.plate_number}
                            className="bg-gk-teal hover:bg-gk-teal-hover text-xs flex-shrink-0"
                          >
                            {manualApprovalLoading === vehicle.plate_number ? 'Approving...' : (
                              <><CheckCircle2 className="w-3 h-3 mr-1" />Approve</>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-blue-600">Total: {villaVehicles.length} registered vehicle(s)</p>
                </div>
              )}

              {(villaDeliveryPasses && villaDeliveryPasses.length > 0) && (
                <div className="mt-3 space-y-2" data-testid="delivery-preapprovals-section">
                  <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
                    <Truck className="w-4 h-4" /> Delivery Pre-Approvals for Villa {villaLookup}
                  </p>
                  <div className="bg-white rounded-lg border border-amber-200 overflow-hidden">
                    <div className="space-y-2 p-2">
                      {villaDeliveryPasses.map((pass) => (
                        <DeliveryPassRow
                          key={pass.id}
                          pass={pass}
                          loading={deliveryEntryLoading === pass.id}
                          onApprove={(plate) => handleDeliveryApprove(pass, plate)}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-amber-700">Enter plate (optional) and tap Approve. Guard name + plate (if entered) will be logged to Airtable as &quot;Delivery pre-approvals&quot;.</p>
                </div>
              )}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-500">Or verify gate pass</span>
            </div>
          </div>

          <Button
            data-testid="scan-qr-button"
            onClick={() => setShowQRScanner(true)}
            className="w-full bg-gk-navy-light hover:bg-gk-navy py-6 text-lg"
          >
            <QrCode className="w-6 h-6 mr-2" />
            Scan QR Code
          </Button>

          <div className="space-y-2">
            <Label htmlFor="access-code">Or Enter 4 / 6-Digit Access Code</Label>
            <div className="flex gap-2">
              <Input
                id="access-code"
                data-testid="access-code-input"
                placeholder="Enter 4 or 6-digit code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="font-mono text-2xl text-center tracking-widest"
                maxLength={6}
                onKeyPress={(e) => e.key === 'Enter' && (accessCode.length === 4 || accessCode.length === 6) && handleVerifyGatePass()}
              />
              <Button
                data-testid="verify-gatepass-button"
                onClick={handleVerifyGatePass}
                disabled={scanning || (accessCode.length !== 4 && accessCode.length !== 6)}
                className="bg-gk-teal hover:bg-gk-teal-hover px-8"
              >
                {scanning ? 'Verifying...' : 'Verify'}
              </Button>
            </div>
            {accessCode.length > 0 && accessCode.length !== 4 && accessCode.length !== 6 && (
              <p className="text-[11px] text-amber-600">Enter exactly 4 or 6 digits.</p>
            )}
          </div>

          {verificationResult && (
            <div
              data-testid="verification-result"
              className={`p-6 rounded-xl border-2 ${
                verificationResult.verified ? 'bg-emerald-50 border-emerald-500' : 'bg-red-50 border-red-500'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                {verificationResult.verified ? (
                  <CheckCircle2 className="w-8 h-8 text-emerald-600 status-indicator" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-600 status-indicator" />
                )}
                <div>
                  <h3 className={`font-bold text-lg ${verificationResult.verified ? 'text-emerald-900' : 'text-red-900'}`}>
                    {verificationResult.verified ? 'ACCESS GRANTED' : 'ACCESS DENIED'}
                  </h3>
                  <p className={verificationResult.verified ? 'text-emerald-700' : 'text-red-700'}>
                    {verificationResult.message}
                  </p>
                </div>
              </div>
              {verificationResult.vehicle && (
                <div className="space-y-1 text-sm">
                  <p className="text-slate-700"><strong>Owner:</strong> {verificationResult.vehicle.owner_name}</p>
                  <p className="text-slate-700"><strong>Villa:</strong> {verificationResult.vehicle.villa_number}</p>
                  <p className="text-slate-700"><strong>Type:</strong> {verificationResult.vehicle.vehicle_type}</p>
                </div>
              )}
              {verificationResult.gate_pass && (
                <div className="space-y-1 text-sm mt-3 pt-3 border-t border-emerald-200">
                  <p className="text-slate-700"><strong>Visitor:</strong> {verificationResult.gate_pass.visitor_name}</p>
                  <p className="text-slate-700"><strong>Phone:</strong> {verificationResult.gate_pass.visitor_phone || 'N/A'}</p>
                  <p className="text-slate-700"><strong>Villa:</strong> {verificationResult.gate_pass.villa_number}</p>
                  <p className="text-slate-700"><strong>Valid Until:</strong> {new Date(verificationResult.gate_pass.valid_until).toLocaleString()}</p>
                  {verificationResult.verified && !gatePassEntryCompleted && (
                    <div className="mt-3 pt-3 border-t border-emerald-200 space-y-3">
                      <div className="p-2 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-800 font-medium">Vehicle number plate is mandatory to complete gate pass entry.</p>
                      </div>
                      <div>
                        <Label htmlFor="gp-vehicle" className="text-emerald-800 font-semibold text-xs uppercase tracking-wide">Visitor's Vehicle Number *</Label>
                        <div className="flex gap-2 mt-1.5">
                          <Input
                            id="gp-vehicle"
                            data-testid="gate-pass-vehicle-input"
                            placeholder="e.g. KA01AB1234"
                            value={gatePassVehicleNumber}
                            onChange={(e) => setGatePassVehicleNumber(e.target.value.toUpperCase())}
                            className="font-mono text-sm uppercase"
                            onKeyPress={(e) => e.key === 'Enter' && gatePassVehicleNumber.trim() && handleRecordGatePassVehicle()}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-emerald-800 font-semibold text-xs uppercase tracking-wide">Visitor Photo (Optional)</Label>
                        <div className="flex gap-2 mt-1.5">
                          <label className="flex-1">
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="hidden"
                              data-testid="gate-pass-photo-file-input"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setGatePassVisitorPhoto(file);
                                }
                              }}
                            />
                            <div
                              data-testid="gate-pass-photo-upload-btn"
                              className="flex items-center justify-center gap-2 p-2 border-2 border-dashed border-emerald-300 rounded-lg cursor-pointer hover:bg-emerald-50 transition-colors text-sm text-emerald-700"
                            >
                              {gatePassVisitorPhoto ? (
                                <><CheckCircle2 className="w-4 h-4" /> {gatePassVisitorPhoto.name}</>
                              ) : (
                                <><Camera className="w-4 h-4" /> Capture / Upload Photo</>
                              )}
                            </div>
                          </label>
                          {gatePassVisitorPhoto && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setGatePassVisitorPhoto(null)}
                              className="text-red-500 hover:text-red-700 shrink-0"
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                      <Button
                        data-testid="record-gate-pass-vehicle-button"
                        onClick={handleRecordGatePassVehicle}
                        disabled={gatePassVehicleLoading || !gatePassVehicleNumber.trim()}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        {gatePassVehicleLoading ? 'Recording...' : 'Complete Entry'}
                      </Button>
                    </div>
                  )}
                  {gatePassEntryCompleted && (
                    <div className="mt-3 pt-3 border-t border-emerald-200">
                      <div className="flex items-center gap-2 text-emerald-700">
                        <CheckCircle2 className="w-5 h-5" />
                        <p className="font-semibold text-sm">Entry recorded successfully</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


function DeliveryPassRow({ pass, loading, onApprove }) {
  const [plate, setPlate] = useState('');

  const submit = () => {
    onApprove(plate.trim().toUpperCase());
    setPlate('');
  };

  return (
    <div
      data-testid={`delivery-pass-${pass.id}`}
      className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-2.5 rounded-lg border border-amber-100 hover:bg-amber-50 transition-colors"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Truck className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-900 truncate">
              {pass.delivery_provider || pass.visitor_name}
            </span>
            <Badge variant="outline" className="text-[10px] capitalize">
              {pass.delivery_category || 'delivery'}
            </Badge>
          </div>
          <p className="text-xs text-slate-500">
            Code: <span className="font-mono font-semibold">{pass.access_code}</span>
            <span className="ml-2">Valid until {new Date(pass.valid_until).toLocaleString()}</span>
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:flex-shrink-0">
        <Input
          value={plate}
          onChange={(e) => setPlate(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !loading) submit();
          }}
          placeholder="Plate (optional)"
          maxLength={20}
          className="h-9 text-xs font-mono uppercase tracking-wider w-32 sm:w-36"
          data-testid={`delivery-plate-input-${pass.id}`}
        />
        <Button
          size="sm"
          data-testid={`delivery-approve-btn-${pass.id}`}
          onClick={submit}
          disabled={loading}
          className="bg-amber-500 hover:bg-amber-600 text-white text-xs"
        >
          {loading ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <><CheckCircle2 className="w-3 h-3 mr-1" />Approve</>
          )}
        </Button>
      </div>
    </div>
  );
}
